import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  getProduct,
  adminAddImages,
  adminUpdateImage,
  adminDeleteImage,
  adminAddVideo,
  adminDeleteVideo,
} from '../../api';

const MAX_VIDEO_MB = 19; // matches the backend limit (nginx allows 20M)
import './AdminLayout.css';
import './AdminImageManager.css';

export default function AdminImageManager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [flash, setFlash] = useState(location.state?.flash || '');

  // Upload state
  const [newFiles, setNewFiles] = useState([]);
  const [newPreviews, setNewPreviews] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef();

  // Editing state
  const [editingId, setEditingId] = useState(null);   // imageId being edited
  const [editAlt, setEditAlt] = useState('');

  // Saving individual image
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Videos
  const videoRef = useRef();
  const [videoProgress, setVideoProgress] = useState(null); // "2/3 · 45%" while uploading

  const load = useCallback(() => {
    getProduct(id).then(r => { setProduct(r.data); setLoading(false); });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (flash) { const t = setTimeout(() => setFlash(''), 3500); return () => clearTimeout(t); }
  }, [flash]);

  /* ── Upload handling ── */
  function onFilePick(files) {
    const list = Array.from(files);
    setNewFiles(prev => [...prev, ...list]);
    setNewPreviews(prev => [
      ...prev,
      ...list.map(f => ({ url: URL.createObjectURL(f), alt: '' })),
    ]);
  }

  function removeNew(i) {
    URL.revokeObjectURL(newPreviews[i].url);
    setNewFiles(p => p.filter((_, idx) => idx !== i));
    setNewPreviews(p => p.filter((_, idx) => idx !== i));
  }

  function updateNewAlt(i, val) {
    setNewPreviews(p => p.map((x, idx) => idx === i ? { ...x, alt: val } : x));
  }

  async function handleUpload() {
    if (!newFiles.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      newFiles.forEach((file, i) => {
        fd.append('images', file);
        fd.append(`imageAlt_${i}`, newPreviews[i]?.alt || product.name);
      });
      const { data } = await adminAddImages(id, fd);
      setProduct(data);
      // Cleanup
      newPreviews.forEach(p => URL.revokeObjectURL(p.url));
      setNewFiles([]);
      setNewPreviews([]);
      setFlash(`✅ ${newFiles.length} image${newFiles.length > 1 ? 's' : ''} uploaded!`);
    } catch (err) {
      alert(err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  /* ── Alt text editing ── */
  function startEdit(img) {
    setEditingId(img._id);
    setEditAlt(img.alt || '');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditAlt('');
  }

  async function saveAlt(imgId) {
    setSavingId(imgId);
    try {
      const { data } = await adminUpdateImage(id, imgId, { alt: editAlt });
      setProduct(data);
      setEditingId(null);
      setFlash('✅ Description saved');
    } catch {
      alert('Failed to save');
    } finally {
      setSavingId(null);
    }
  }

  /* ── Set primary ── */
  async function setPrimary(imgId) {
    setSavingId(imgId);
    try {
      const { data } = await adminUpdateImage(id, imgId, { isPrimary: true });
      setProduct(data);
      setFlash('✅ Primary image updated');
    } catch {
      alert('Failed');
    } finally {
      setSavingId(null);
    }
  }

  /* ── Delete image ── */
  async function handleDelete(imgId, isPrimary) {
    const confirmMsg = isPrimary
      ? 'This is the primary image. Delete it? The next image will become primary.'
      : 'Delete this image?';
    if (!window.confirm(confirmMsg)) return;
    setDeletingId(imgId);
    try {
      const { data } = await adminDeleteImage(id, imgId);
      setProduct(data);
      setFlash('🗑️ Image deleted');
    } catch {
      alert('Delete failed');
    } finally {
      setDeletingId(null);
    }
  }

  /* ── Videos ── */
  async function handleVideoPick(fileList) {
    const files = Array.from(fileList);
    if (videoRef.current) videoRef.current.value = '';
    const tooBig = files.filter(f => f.size > MAX_VIDEO_MB * 1024 * 1024);
    if (tooBig.length) {
      alert(`These videos are over ${MAX_VIDEO_MB} MB:\n${tooBig.map(f => f.name).join('\n')}\n\nCompress or trim them and try again.`);
      return;
    }
    // One request per video keeps each upload under the server's size limit
    let uploaded = 0;
    try {
      for (const [i, file] of files.entries()) {
        const fd = new FormData();
        fd.append('video', file);
        const { data } = await adminAddVideo(id, fd, e => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setVideoProgress(`${i + 1}/${files.length} · ${pct}%`);
        });
        setProduct(data);
        uploaded++;
      }
      setFlash(`✅ ${uploaded} video${uploaded > 1 ? 's' : ''} uploaded!`);
    } catch (err) {
      alert(err.response?.data?.message || 'Video upload failed');
    } finally {
      setVideoProgress(null);
    }
  }

  async function handleDeleteVideo(videoId) {
    if (!window.confirm('Delete this video?')) return;
    setDeletingId(videoId);
    try {
      const { data } = await adminDeleteVideo(id, videoId);
      setProduct(data);
      setFlash('🗑️ Video deleted');
    } catch {
      alert('Delete failed');
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) return <div className="admin-loading">Loading…</div>;
  if (!product) return null;

  const images = product.images || [];
  const videos = product.videos || [];

  return (
    <div style={{ paddingBottom: 40 }}>
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Image Manager</h1>
          <p className="admin-page-sub">
            <span style={{ color: '#9ca3af' }}>Product: </span>
            <strong>{product.name}</strong>
            <span style={{ marginLeft: 8, color: '#9ca3af' }}>{images.length} image{images.length !== 1 ? 's' : ''}</span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn-secondary" onClick={() => navigate(`/admin/products/${id}`)}>
            ← Edit Product
          </button>
          <button className="btn-secondary" onClick={() => navigate('/admin/products')}>
            All Products
          </button>
        </div>
      </div>

      {/* Flash message */}
      {flash && (
        <div className="im-flash">
          {flash}
        </div>
      )}

      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* ── Upload section ── */}
        <div className="admin-card" style={{ padding: 24 }}>
          <h3 className="im-section-title">Upload New Images</h3>

          <div
            className={`im-dropzone ${dragOver ? 'drag-over' : ''}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); onFilePick(e.dataTransfer.files); }}
          >
            <span className="im-drop-icon">📸</span>
            <p>Click or drag & drop images here</p>
            <span className="im-drop-hint">JPG, PNG, WebP, GIF — max 5MB each · multiple files OK</span>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={e => onFilePick(e.target.files)}
          />

          {/* New file previews */}
          {newPreviews.length > 0 && (
            <>
              <div className="im-preview-grid">
                {newPreviews.map((p, i) => (
                  <div key={i} className="im-preview-item">
                    <div className="im-preview-img-wrap">
                      <img src={p.url} alt="preview" />
                      <button
                        type="button"
                        className="im-remove-btn"
                        onClick={() => removeNew(i)}
                      >✕</button>
                    </div>
                    <div className="im-alt-wrap">
                      <label>Image description</label>
                      <input
                        type="text"
                        value={p.alt}
                        onChange={e => updateNewAlt(i, e.target.value)}
                        placeholder="Describe this image…"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
                <button
                  className="btn-primary"
                  onClick={handleUpload}
                  disabled={uploading}
                >
                  {uploading ? '⏳ Uploading…' : `⬆️ Upload ${newFiles.length} Image${newFiles.length > 1 ? 's' : ''}`}
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => {
                    newPreviews.forEach(p => URL.revokeObjectURL(p.url));
                    setNewFiles([]);
                    setNewPreviews([]);
                  }}
                >
                  Clear
                </button>
                <span style={{ fontSize: 13, color: '#9ca3af' }}>
                  Each image can have its own description used for alt text and SEO.
                </span>
              </div>
            </>
          )}
        </div>

        {/* ── Existing images ── */}
        <div className="admin-card" style={{ padding: 24 }}>
          <h3 className="im-section-title">
            Current Images
            {images.length > 0 && (
              <span style={{ marginLeft: 8, fontSize: 13, color: '#9ca3af', fontWeight: 400 }}>
                — click ⭐ to set as primary (shown first in store)
              </span>
            )}
          </h3>

          {images.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 0' }}>
              <span>🖼️</span>
              <p>No images yet. Upload some above.</p>
            </div>
          ) : (
            <div className="im-existing-grid">
              {images.map((img, index) => (
                <div key={img._id} className={`im-existing-item ${img.isPrimary ? 'is-primary' : ''}`}>
                  {/* Image */}
                  <div className="im-existing-img-wrap">
                    <img src={img.url} alt={img.alt || 'Product image'} />
                    {img.isPrimary && <span className="im-primary-badge">⭐ Primary</span>}
                    <div className="im-img-overlay">
                      <span className="im-img-index">#{index + 1}</span>
                    </div>
                  </div>

                  {/* Alt text row */}
                  <div className="im-existing-body">
                    {editingId === img._id ? (
                      /* Editing mode */
                      <div className="im-edit-row">
                        <div className="im-alt-wrap">
                          <label>Image description (alt text)</label>
                          <textarea
                            value={editAlt}
                            onChange={e => setEditAlt(e.target.value)}
                            rows={2}
                            placeholder="Describe what's in this image…"
                            autoFocus
                          />
                        </div>
                        <div className="im-edit-actions">
                          <button
                            className="btn-primary"
                            style={{ fontSize: 13, padding: '7px 14px' }}
                            onClick={() => saveAlt(img._id)}
                            disabled={savingId === img._id}
                          >
                            {savingId === img._id ? 'Saving…' : '✓ Save'}
                          </button>
                          <button className="btn-secondary" style={{ fontSize: 13, padding: '6px 12px' }} onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* View mode */
                      <div className="im-view-row">
                        <div className="im-alt-text">
                          {img.alt
                            ? <span className="im-alt-value">"{img.alt}"</span>
                            : <span className="im-alt-empty">No description — click Edit to add one</span>
                          }
                        </div>
                        <div className="im-action-row">
                          <button
                            className="btn-icon"
                            title="Edit description"
                            onClick={() => startEdit(img)}
                          >✏️ Edit</button>

                          {!img.isPrimary && (
                            <button
                              className="btn-icon"
                              title="Set as primary image"
                              disabled={savingId === img._id}
                              onClick={() => setPrimary(img._id)}
                            >
                              {savingId === img._id ? '⏳' : '⭐ Set Primary'}
                            </button>
                          )}

                          <button
                            className="btn-danger"
                            title="Delete image"
                            disabled={deletingId === img._id}
                            onClick={() => handleDelete(img._id, img.isPrimary)}
                          >
                            {deletingId === img._id ? '…' : '🗑️ Delete'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Videos ── */}
        <div className="admin-card" style={{ padding: 24 }}>
          <h3 className="im-section-title">
            Product Videos
            <span style={{ marginLeft: 8, fontSize: 13, color: '#9ca3af', fontWeight: 400 }}>
              — shown after the images on the product page
            </span>
          </h3>

          <div
            className={`im-dropzone ${videoProgress ? 'drag-over' : ''}`}
            onClick={() => !videoProgress && videoRef.current?.click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); if (!videoProgress) handleVideoPick(e.dataTransfer.files); }}
          >
            <span className="im-drop-icon">🎬</span>
            <p>{videoProgress ? `⏳ Uploading ${videoProgress}` : 'Click or drag & drop videos here'}</p>
            <span className="im-drop-hint">MP4, WebM, MOV — max {MAX_VIDEO_MB}MB each · MP4 plays on every device</span>
          </div>
          <input
            ref={videoRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            multiple
            hidden
            onChange={e => handleVideoPick(e.target.files)}
          />

          {videos.length > 0 && (
            <div className="im-existing-grid" style={{ marginTop: 20 }}>
              {videos.map((v, index) => (
                <div key={v._id} className="im-existing-item">
                  <div className="im-existing-img-wrap">
                    <video src={v.url} controls preload="metadata" playsInline />
                    <div className="im-img-overlay">
                      <span className="im-img-index">🎬 #{index + 1}</span>
                    </div>
                  </div>
                  <div className="im-existing-body">
                    <div className="im-action-row">
                      <button
                        className="btn-danger"
                        title="Delete video"
                        disabled={deletingId === v._id}
                        onClick={() => handleDeleteVideo(v._id)}
                      >
                        {deletingId === v._id ? '…' : '🗑️ Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick tip */}
        <div className="im-tip">
          <strong>💡 Tips:</strong>
          Good image descriptions improve SEO and accessibility. Be specific: <em>"3D printed gym plate keychain in red, showing front face with custom name engraved"</em> is much better than <em>"keychain"</em>.
          The <strong>primary image</strong> (⭐) is shown in product listings and as the first image on the product page.
        </div>
      </div>
    </div>
  );
}
